import type { OverlayEventDetail } from '@ionic/core'
import { useQuery } from '@tanstack/react-query'
import { ParseKeys } from 'i18next'
import { caretDown } from 'ionicons/icons'
import { useMemo } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Option } from '~/shared/config/constants'
import { useParserI18nMessage } from '~/shared/lib/i18n'
import { useDrawer } from '~/shared/ui/Drawer'
import { ProgressOverlay } from '~/shared/ui/Loaders'
import {
  TextFieldElementWrapper,
  IonIcon,
  TextFieldLabel,
  TextFieldValue,
  IonActionSheet,
  ErrorMessage,
} from './styled'

type DrawerSelectProps = {
  defaultOptions?: Option[]
  name: string
  label: ParseKeys
  title: ParseKeys
  queryKey?: string
  fetchOptions?: () => Promise<Option[]>
  disabled?: boolean
  dataTestId?: string
  loading?: boolean
  validation?: ControllerProps['rules']
}

export function DrawerSelect({
  defaultOptions,
  name,
  label,
  title,
  queryKey,
  fetchOptions,
  disabled,
  dataTestId,
  loading,
  validation = {},
}: DrawerSelectProps) {
  const { t } = useTranslation()
  const { parserI18nMessage } = useParserI18nMessage()
  const { openDrawer, closeDrawer, isDrawerShow } = useDrawer()

  const handleOptionsFetch = () => {
    return fetchOptions?.() || []
  }

  const { isFetching, data } = useQuery<Option[]>(
    ['drawer-select', name, queryKey],
    () => handleOptionsFetch(),
    {
      enabled: Boolean(fetchOptions),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  )

  const options = useMemo(
    () => defaultOptions ?? data ?? [],
    [data, defaultOptions],
  )

  const handleDriverOpen = () => {
    if (disabled) return
    openDrawer()
  }

  return (
    <div data-testid={dataTestId}>
      <Controller
        name={name}
        rules={validation}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <ProgressOverlay
            loading={loading || (!defaultOptions && isFetching)}
            spinnerProps={{ style: { size: 18 } }}
          >
            <TextFieldElementWrapper
              $disabled={disabled}
              onClick={handleDriverOpen}
            >
              <TextFieldLabel
                data-testid='drawer-select-label'
                $isTop={Boolean(value?.label)}
              >
                {t(label) as string}
              </TextFieldLabel>
              <TextFieldValue data-testid='drawer-select-value'>
                {t(value?.label)}
              </TextFieldValue>

              {!disabled && (
                <IonIcon icon={caretDown} $reverse={isDrawerShow} />
              )}
            </TextFieldElementWrapper>

            {error && !value && (
              <ErrorMessage data-testid='drawer-select-error'>
                {parserI18nMessage(error.message)}
              </ErrorMessage>
            )}
            <IonActionSheet
              mode='md'
              isOpen={isDrawerShow}
              header={t(title) as string}
              buttons={options.map((option) => ({
                text: t(option.label as ParseKeys) as string,
                role: option.id === value?.id ? 'selected' : 'destructive',
                data: option,
              }))}
              onIonActionSheetWillDismiss={(
                e: CustomEvent<OverlayEventDetail>,
              ) => {
                e.detail.data && onChange(e.detail.data)
                closeDrawer()
              }}
            />
          </ProgressOverlay>
        )}
      />
    </div>
  )
}
